/**
 * What is ticked — list engine v3 §2.
 *
 * A selection is one of two things, and the difference is the whole feature.
 *
 * **`ids`** is what somebody ticked, one row at a time, across any number of
 * pages. **`matching`** is "everything this filter and this search describe",
 * which is a *mode* rather than 422 ids: nobody wants to send 422 uuids over
 * the wire to deactivate 422 contacts, and more to the point the client does
 * not have them — it has fifty. So `matching` carries only what was *un*ticked
 * afterwards, and the server works the rest out from the same question the
 * list asked (§3.5).
 *
 * That is also why unticking a row in `matching` mode adds to `excluded`
 * rather than converting the whole thing to ids. "All 128 except two" stays
 * one honest sentence, and stays cheap.
 */
export type Selection =
  /** Ticked one by one, on any page. */
  | { mode: 'ids'; ids: ReadonlySet<string> }
  /** Everything the filter and search match, except these. */
  | { mode: 'matching'; excluded: ReadonlySet<string> }

export const noSelection: Selection = { mode: 'ids', ids: new Set() }

/**
 * How many are selected.
 *
 * `visibleCount` is what the list says it is showing — 128 of 422 when a
 * filter is on — so `matching` counts against the filtered number and never
 * against the tenant's total. It is the same number the slot prints and the
 * same one the server is told to `expect`.
 */
export function selectedCount(selection: Selection, visibleCount: number): number {
  return selection.mode === 'ids'
    ? selection.ids.size
    : Math.max(0, visibleCount - selection.excluded.size)
}

export const isSelected = (selection: Selection, id: string): boolean =>
  selection.mode === 'ids' ? selection.ids.has(id) : !selection.excluded.has(id)

export const anySelected = (selection: Selection, visibleCount: number): boolean =>
  selectedCount(selection, visibleCount) > 0

/**
 * The ceiling on hand-ticking.
 *
 * Not a performance number — a `Set` of ten thousand strings is nothing. It is
 * the point past which ticking rows one at a time has stopped being the right
 * tool and "Select all N" is: the server's `ids` selector is capped at the
 * same figure (§3.5), so the two cannot disagree about what is sendable.
 */
export const MAX_TICKS = 1000

/** Tick or untick one row, in whichever mode the selection is in. */
export function toggleOne(selection: Selection, id: string): Selection {
  if (selection.mode === 'matching') {
    const excluded = new Set(selection.excluded)
    if (excluded.has(id)) excluded.delete(id)
    else excluded.add(id)
    return { mode: 'matching', excluded }
  }
  const ids = new Set(selection.ids)
  if (ids.has(id)) ids.delete(id)
  else if (ids.size < MAX_TICKS) ids.add(id)
  return { mode: 'ids', ids }
}

/** Tick a span, without unticking anything outside it. */
export function selectMany(selection: Selection, span: readonly string[]): Selection {
  if (selection.mode === 'matching') {
    const excluded = new Set(selection.excluded)
    for (const id of span) excluded.delete(id)
    return { mode: 'matching', excluded }
  }
  const ids = new Set(selection.ids)
  for (const id of span) { if (ids.size < MAX_TICKS) ids.add(id) }
  return { mode: 'ids', ids }
}

export function deselectMany(selection: Selection, span: readonly string[]): Selection {
  if (selection.mode === 'matching') {
    const excluded = new Set(selection.excluded)
    for (const id of span) excluded.add(id)
    return { mode: 'matching', excluded }
  }
  const ids = new Set(selection.ids)
  for (const id of span) ids.delete(id)
  return { mode: 'ids', ids }
}

/**
 * The page's tick box: ticked when every row on it is, half when some are.
 *
 * Computed from the rows on screen in both modes, because that is the question
 * the box is answering — "is this page in?" — and in `matching` mode the
 * answer is still no when two of its rows have been left out.
 */
export function pageState(
  selection: Selection, pageIds: readonly string[],
): { checked: boolean; indeterminate: boolean } {
  if (pageIds.length === 0) return { checked: false, indeterminate: false }
  let on = 0
  for (const id of pageIds) { if (isSelected(selection, id)) on += 1 }
  return { checked: on === pageIds.length, indeterminate: on > 0 && on < pageIds.length }
}
